/**
 * Activity reporting for a running campaign step.
 *
 * A spinner alone says nothing about whether work is happening. Two signals do:
 *
 * 1. Batch progress, from the `batchProgress` event published by the platform's
 *    own batching loop. Present only for batched operations.
 * 2. Elapsed time on the active step, which advances even when nothing is batched.
 *
 * **Silence is deliberately not treated as a symptom.** An earlier version warned
 * after 60s without a server message, and it fired on every long step: docking
 * ran 2m40s with the socket quiet for 2m39s, and the panel called that "no
 * response for 2m 39s". `ServerConnection.monitorConnection` says why — "during a
 * long server computation (eg docking), there won't be any inbound packets
 * processed." Quiet is the expected state while BFD computes, so reporting it as
 * trouble manufactures the exact alarm this feature exists to remove.
 *
 * The client cannot distinguish "computing" from "hung" on its own; only a
 * server-side signal can, which is what batch progress provides where it exists.
 * So the honest contract is: report what is known, and raise a warning only on a
 * fact — the connection actually reporting itself down. The platform keepalive
 * already detects that and drives the flag.
 *
 * Everything here is pure so it can be tested without a DOM.
 */

/** Below this, elapsed time is noise rather than reassurance. */
export const ELAPSED_VISIBLE_AFTER_MS = 3000;

/** @param {number} ms */
export function formatDuration(ms) {
    if (!Number.isFinite(ms) || ms < 0) return '';
    const totalSeconds = Math.floor(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * A `batchProgress` payload is only worth showing when it describes more than one
 * unit of work; `1/1` tells the reader nothing a spinner did not.
 */
export function formatProgress(progress) {
    if (!progress || typeof progress !== 'object') return '';
    const done = Number(progress.itemsDone);
    const total = Number(progress.itemCount);
    if (!Number.isFinite(done) || !Number.isFinite(total) || total <= 1) return '';
    const clamped = Math.max(0, Math.min(done, total));
    const label = typeof progress.label === 'string' ? progress.label.trim() : '';
    return label ? `${label} ${clamped}/${total}` : `${clamped}/${total}`;
}

/**
 * Compose the one-line activity suffix shown beside a running step.
 *
 * @param {object} input
 * @param {object|null} [input.progress] latest `batchProgress` payload
 * @param {number|null} [input.elapsedMs] time on the active step
 * @param {boolean|null} [input.connected] connection state; null when unknown
 * @returns {{ text: string, tone: 'info'|'warning', disconnected: boolean }}
 */
export function campaignActivityLine({
    progress = null,
    elapsedMs = null,
    connected = null,
} = {}) {
    const parts = [];

    const progressText = formatProgress(progress);
    if (progressText) parts.push(progressText);

    if (Number.isFinite(elapsedMs) && elapsedMs >= ELAPSED_VISIBLE_AFTER_MS) {
        parts.push(formatDuration(elapsedMs));
    }

    // Only `false` is a fact. `null` means the panel could not read the
    // connection, which is not the same as the connection being down.
    const disconnected = connected === false;
    if (disconnected) parts.push('connection lost — the step cannot complete until it returns');

    return { text: parts.join(' · '), tone: disconnected ? 'warning' : 'info', disconnected };
}

/**
 * Read the connection state off a data connection or its connector.
 *
 * `isConnected()` is a public method on the platform connection and is the only
 * client-side fact about liveness: the platform keepalive probes during quiet
 * periods and flips it on a real drop. Returns null when no connection object can
 * be read, so callers report nothing rather than inventing a failure.
 *
 * Note what is deliberately *not* read here: `lastMessageTime`. Time since the
 * last message looks like a liveness signal and is not one — BFD goes silent for
 * minutes during normal computation.
 *
 * @returns {{ connected: boolean|null }}
 */
export function connectionActivity(dataConnection) {
    const candidates = [dataConnection, dataConnection?.connector, dataConnection?.dataConnection];
    for (const candidate of candidates) {
        if (typeof candidate?.isConnected === 'function') {
            return { connected: candidate.isConnected() === true };
        }
    }
    return { connected: null };
}
